/**
 * Every user-visible string in the app, keyed. Transcribed from the primary design
 * (`design/project/Estimate Management App.dc.html`). Interpolated strings are functions so the
 * shape of the sentence stays here, not in a component.
 *
 * Rules (design-system readme, "Content fundamentals"): sentence case, plain words, one idea per
 * sentence, no "sync"/"upload"/"retry", errors state the fix, buttons are verbs, never emoji.
 */
/** `bins` as a unit, lowercase, always with the number. */
export const bins = (n) => `${n} bins`;
export const messages = {
    app: {
        name: 'Fruitways Harvest Estimation',
        shortName: 'Estimates',
    },
    common: {
        cancel: 'Cancel',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        edit: 'Edit',
        delete: 'Delete',
        remove: 'Remove',
        review: 'Review',
        /** Field label "Week", also used as the row title on review and detail. */
        week: 'Week',
        season: 'Season',
        productionUnit: 'Production unit',
        variety: 'Variety',
        day: 'Day',
        expectedBins: 'Expected bins',
        weekN: (n) => `Week ${n}`,
        /** `7–12 Sep 2026 · Season 2026/2027` */
        weekDatesSeason: (dates, season) => `${dates} · Season ${season}`,
        /** `36 · 7–12 Sep 2026` */
        weekNumberDates: (n, dates) => `${n} · ${dates}`,
    },
    header: {
        /** Uppercase subtitle under a screen title. */
        seasonWeek: (season, week) => `SEASON ${season} · WEEK ${week}`,
        season: (season) => `SEASON ${season}`,
        farmWeek: (farm, week) => `${farm.toUpperCase()} · WEEK ${week}`,
        profile: 'Grower',
        profileAdmin: 'Admin',
        roleAdmin: 'Fruitways admin',
        masterData: 'MASTER DATA',
        logOut: 'Log out',
    },
    tabs: {
        home: 'Home',
        estimates: 'Estimates',
        waiting: 'Waiting',
        data: 'Data',
    },
    banner: {
        offline: 'No signal. Your work is saved on this phone.',
        /** Admin master data screens: changes need signal and are never queued. */
        offlineData: 'No signal. You need signal to make changes.',
        /** Admin estimates list: read from the phone, may be behind the pack house. */
        offlineEstimates: 'No signal. Estimates may be out of date.',
    },
    status: {
        submitted: 'Submitted',
        edited: 'Edited',
        queued: 'Waiting for signal',
    },
    login: {
        username: 'Username / Email',
        password: 'Password',
        remember: 'Remember password',
        logIn: 'Log in',
        forgotPassword: 'Forgot password?',
        failed: 'Incorrect username or password. Please try again.',
        usernameRequired: 'Enter your username.',
        passwordRequired: 'Enter your password.',
    },
    forgot: {
        title: 'Forgot password',
        intro: 'Enter your username. Fruitways will send you a link to set a new password.',
        send: 'Send reset link',
        sent: 'Check your email. The link works for one hour.',
    },
    dashboard: {
        title: 'Estimates',
        dueBody: 'No estimate submitted yet. Capture expected bins for Monday to Saturday.',
        submittedBody: (total, farm) => `${bins(total)} submitted for ${farm}. You can change it until Wednesday.`,
        submittedBodyGeneric: 'An estimate is in for this week. You can change it until Wednesday.',
        /** One stable label. It never becomes "Edit estimate"; editing is reached from the estimate. */
        capture: 'Submit estimate',
        /*
         * The week card speaks to how far through the grower is: none of their farms in, some, or
         * all. The third pairs with the disabled button, so it says when submitting comes back.
         */
        none: {
            title: 'No estimates submitted yet',
            body: 'Capture your expected bins for Monday to Saturday for each of your managed farms.',
        },
        some: {
            title: 'Estimates partially submitted',
            body: "You've submitted estimates for some farms. Please complete the remaining farm estimates before Wednesday's cut-off.",
        },
        all: {
            title: 'All estimates submitted',
            body: "You've submitted estimates for all your managed farms for this submission period. You can submit your next estimates from Thursday.",
        },
        recent: 'Submitted estimates',
        emptyTitle: 'No estimates submitted',
        emptyBody: 'Your submitted estimates will show here.',
    },
    list: {
        title: 'All estimates',
        emptyTitle: 'No estimates submitted',
        emptyBody: "Capture this week's estimate from the dashboard.",
    },
    capture: {
        title: 'Capture estimate',
        editTitle: 'Edit estimate',
        step1: 'Step 1 of 3 · Estimate setup',
        step2: 'Step 2 of 3 · Expected bins',
        step3: 'Step 3 of 3 · Check the numbers',
        editingLine: 'Editing a submitted estimate',
        selectFarm: 'Select farm',
        /** Step 1 with nothing left to capture for: every farm is already in for the open week. */
        allFarmsIn: 'Every farm already has an estimate for this week. Open an estimate to change it.',
        selectVarieties: 'Select varieties',
        chooseOneOrMore: (farm) => `${farm} · choose one or more`,
        step1Error: 'Choose a farm and at least one variety.',
        previousVariety: 'Previous variety',
        nextVariety: 'Next variety',
        position: (index, count) => `${index} of ${count}`,
        varietyTotal: (code) => `${code} total`,
        weekTotal: 'Week total',
        binsRequired: 'Enter a number of bins.',
    },
    addVarieties: {
        title: 'Add varieties',
        addButtonLabel: 'Add variety',
        hint: 'Tap to select. Tap again to unselect.',
        confirm: (count) => (count > 1 ? `Add ${count} varieties` : 'Add variety'),
        added: (codes) => codes.length === 1
            ? `${codes[0]} added to this estimate.`
            : `${codes.length} varieties added to this estimate.`,
        removed: (code) => `${code} removed from this estimate.`,
    },
    review: {
        title: 'Review estimate',
        editTitle: 'Review changes',
        checkChanges: 'Check your changes',
        section: 'Review',
        table: {
            variety: 'Var',
            days: ['M', 'T', 'W', 'T', 'F', 'S'],
            total: 'Tot',
            /** Leftmost cell of the row that sums every variety for each day. */
            dayTotal: 'Total',
            dayTotalRowLabel: 'Bins per day, all varieties',
        },
        totalExpected: 'Total expected',
        offlineNote: 'No signal now. This estimate will send when you have signal.',
        submit: 'Submit estimate',
        update: 'Update estimate',
        backToEntry: 'Back to entry',
    },
    success: {
        submittedTitle: 'Estimate submitted successfully',
        submittedBody: 'Fruitways has your expected bins for this week.',
        savedTitle: 'Saved on this phone',
        savedBody: 'It will send to Fruitways when you have signal.',
        updatedTitle: 'Estimate updated successfully',
        updatedBody: 'Your changes are saved. The estimate now shows as edited.',
        deletedTitle: 'Estimate deleted successfully',
        deletedBody: 'You can capture a new estimate for this farm and week.',
        meta: (farm, week, total) => `${farm} · Week ${week} · ${bins(total)}`,
        backToDashboard: 'Back to dashboard',
    },
    detail: {
        fallbackTitle: 'Estimate',
        submitted: 'Submitted',
        totalSubmitted: 'Total submitted',
    },
    queue: {
        title: 'Waiting to send',
        emptyTitle: 'Nothing waiting',
        emptyBody: 'Every estimate on this phone has been sent.',
        sendAll: 'Send all now',
        sent: 'Sent to Fruitways.',
        sendFailed: 'Sending failed. Try again when you have signal.',
        rowTitle: (week, farm) => `Week ${week} · ${farm}`,
    },
    dialogs: {
        logOut: {
            title: 'Log out?',
            body: 'Are you sure you want to log out?',
            unsavedTitle: 'You have unsaved changes',
            unsavedBody: 'Are you sure you want to log out? Your changes will be lost.',
            confirm: 'Log out',
        },
        deleteEstimate: {
            title: 'Delete estimate?',
            body: (farm, week) => `Are you sure you want to delete this submitted estimate for ${farm} – Week ${week}?`,
            confirm: 'Delete estimate',
        },
        removeVariety: {
            title: 'Remove variety?',
            body: (code) => `${code} already has an estimate submitted for this week. If you remove ${code}, all estimate values for this variety will be deleted from the estimate.`,
            note: (code) => `Are you sure you want to remove ${code}?`,
            confirm: 'Remove variety',
        },
    },
    /** Everything only a Fruitways admin sees. */
    admin: {
        filter: {
            label: 'Filter',
            clear: 'Clear filters',
            grower: 'Grower',
            farm: 'Farm',
            variety: 'Variety',
            week: 'Week',
            allGrowers: 'All growers',
            allFarms: 'All farms',
            allVarieties: 'All varieties',
            allWeeks: 'All weeks',
            chooseGrower: 'Choose grower',
            chooseFarm: 'Choose farm',
            chooseVariety: 'Choose variety',
            chooseWeek: 'Choose week',
            search: 'Search',
            searchGrower: 'Grower or farm name',
            searchFarm: 'Farm, PUC or grower',
            searchVariety: 'Variety code',
            searchWeek: 'Week number',
            noMatch: (q) => `Nothing matches “${q}”. Check the spelling.`,
            growers: (n) => (n === 1 ? '1 grower' : `${n} growers`),
            farms: (n) => (n === 1 ? '1 farm' : `${n} farms`),
            varieties: (n) => (n === 1 ? '1 variety' : `${n} varieties`),
            weeks: (n) => (n === 1 ? '1 week' : `${n} weeks`),
            weeksWithHarvest: (n) => `${n === 1 ? '1 week' : `${n} weeks`} with varieties in harvest`,
            growerDetail: (farmCount, farmNames) => `${farmCount === 1 ? '1 farm' : `${farmCount} farms`} · ${farmNames}`,
            pucDetail: (puc) => `PUC ${puc}`,
            pucGrowerDetail: (puc, grower) => `PUC ${puc} · ${grower}`,
            allFarmsDetail: (who, n) => `${who} · ${n === 1 ? '1 farm' : `${n} farms`}`,
        },
        home: {
            scopeRecent: (who) => `Latest 2 estimates for ${who}.`,
            scopeAll: (who) => `All estimates for ${who}.`,
            whoEachFarm: 'each farm',
            whoGrower: (grower) => `each farm of ${grower}`,
            quietAll: (quiet, total) => `${quiet} of ${total} farms have no estimates yet. Choose a grower to see all their farms.`,
            quietGrower: (quiet, total) => `${quiet} of ${total} farms ${quiet === 1 ? 'has' : 'have'} no estimates yet.`,
            noEstimates: 'No estimates this season',
            unlinked: 'No grower linked',
            emptyBody: 'Estimates from growers will show here.',
        },
        capture: {
            forGrower: 'You are capturing for a grower. Choose the grower first.',
            selectGrower: 'Select grower',
            chooseGrower: 'Choose a grower',
            growerRequired: 'Choose a grower, a farm and at least one variety.',
        },
        data: {
            title: 'Data management',
            intro: 'Master data Fruitways keeps for harvest planning.',
            section: 'Master data',
            harvestWeeks: 'Variety harvest weeks',
            harvestWeeksHint: 'Which weeks each variety is in harvest, per season.',
            harvestWeekCount: (n, season) => `${n === 1 ? '1 harvest week' : `${n} harvest weeks`} in ${season}`,
            replaceNote: 'Importing an Excel file replaces every variety harvest week.',
            userFarms: 'User & Farm Management',
            userFarmsHint: 'Who captures for each farm, and how they sign in.',
            userFarmCount: (users, farms) => `${users === 1 ? '1 user' : `${users} users`}, ${farms === 1 ? '1 farm' : `${farms} farms`}`,
            loadFailed: 'Master data could not load. Try again when you have signal.',
            saveFailed: 'Not saved. Try again when you have signal.',
            changedElsewhere: 'Someone else changed this data. Check it and try again.',
        },
        hw: {
            title: 'Variety harvest weeks',
            count: (n, varieties) => `${n === 1 ? '1 harvest week' : `${n} harvest weeks`} · ${varieties === 1 ? '1 variety' : `${varieties} varieties`}`,
            /** Column headings of the list. */
            week: 'Week',
            variety: 'Variety',
            emptyTitle: (season) => `No harvest weeks for ${season}`,
            emptyBody: 'Import an Excel file with the weeks each variety is in harvest.',
            noMatchTitle: 'Nothing matches this filter',
            noMatchBody: 'Clear the filters to see every week.',
            importFile: 'Import from Excel',
            weeksSummary: (runs, count) => `${count === 1 ? 'Week' : 'Weeks'} ${runs}`,
            noHarvestLine: (season) => `No harvest weeks in ${season}`,
        },
        userFarms: {
            title: 'User & Farm Management',
            intro: 'One row per farm: who captures for it, and how they sign in. Someone who manages several farms appears once per farm.',
            download: 'Download latest user and farm list',
            downloadHint: 'Every user and farm there is now. Passwords are never included: fill one in only to set or change it.',
            file: 'user-and-farm-management.xlsx',
            headers: [
                'Farm Name',
                'PUC Code',
                'Display_name',
                'User_name',
                'Password',
                'Role',
                'Active',
            ],
            saved: (n) => n === 1 ? '1 row saved to your downloads.' : `${n} rows saved to your downloads.`,
            changeFile: 'Change file',
            apply: (n) => (n === 1 ? 'Apply 1 row' : `Apply ${n} rows`),
            applied: 'User farms updated.',
            deactivating: (n) => n === 1
                ? '1 assignment is not in the file and becomes inactive.'
                : `${n} assignments are not in the file and become inactive.`,
            nothingRemoved: 'Nothing is deleted. What the file leaves out becomes inactive.',
            noActiveAdmin: 'The file keeps no active admin. Keep at least one row with role admin and Active 1, then choose the file again.',
            newCounts: (users, farms) => `${users} new ${users === 1 ? 'user' : 'users'}, ${farms} new ${farms === 1 ? 'farm' : 'farms'}`,
            reasons: {
                missing_value: 'Fill in the farm, PUC, name and user name.',
                invalid_role: 'Role must be admin, staff or grower.',
                invalid_active: 'Active must be 1 or 0.',
                duplicate_assignment: 'This user is already on this farm earlier in the file.',
                password_required: 'A new user needs a password.',
            },
            emptyTitle: 'No users yet',
            emptyBody: 'Add them in the user and farm file, then choose it here.',
            columns: {
                farm: 'Farm',
                puc: 'PUC',
                user: 'User name',
                role: 'Role',
            },
            inactive: 'Inactive',
        },
        import: {
            title: 'Import variety harvest weeks',
            intro: 'Each row is one season, one variety and one week. The file replaces every variety harvest week there is now.',
            step1: '1 · Download what is in now',
            step2: '2 · Choose your file',
            step3: '3 · Check the rows',
            template: 'variety-harvest-weeks.xlsx',
            columns: 'Columns: Season, Variety code, Week number',
            /** Column headings written into the template and expected in the first row. */
            headers: ['Season', 'Variety code', 'Week number'],
            getTemplate: 'Download latest variety harvest estimates',
            /** The file holds every harvest week, because an import replaces every harvest week. */
            templateHint: 'Every variety harvest week there is now, in the columns an import expects. Change it, add rows, then put it back.',
            templateSaved: (n) => n === 0
                ? 'Empty file saved to your downloads. Add rows, then put it back.'
                : `${n === 1 ? '1 row' : `${n} rows`} saved to your downloads.`,
            noFile: 'No file chosen',
            fileHint: 'Excel files only (.xlsx). Keep the column names in the first row.',
            chooseFile: 'Choose Excel file',
            changeFile: 'Change file',
            checking: 'Checking the rows',
            rows: (n) => (n === 1 ? '1 row' : `${n} rows`),
            notImported: 'Not imported',
            unreadableTitle: 'Check the column names',
            unreadable: 'The first row needs Season, Variety code and Week number. Use the template, then choose the file again.',
            notExcelTitle: 'Choose an Excel file',
            notExcel: 'This file is not an Excel file (.xlsx). Save it as .xlsx, then choose it again.',
            emptyFile: 'This file has no rows under the column names. Add rows, then choose it again.',
            ready: (n) => n === 1 ? '1 row ready to import' : `${n} rows ready to import`,
            toFix: (n) => (n === 1 ? '1 row needs fixing' : `${n} rows need fixing`),
            willSkip: (n) => n === 1 ? '1 repeated row is imported once.' : `${n} repeated rows are imported once.`,
            replaces: (n) => n === 1
                ? 'Replaces the 1 variety harvest week there is now.'
                : `Replaces the ${n} variety harvest weeks there are now.`,
            noReady: 'No rows to import. Fix the file, then choose it again.',
            rowsToFix: 'Rows to fix',
            skipped: 'Repeated rows',
            readyToImport: 'Ready to import',
            fixNote: 'Nothing is replaced while rows need fixing. Fix them in the file, then choose it again.',
            missingSeasons: (seasons) => `The file has no rows for season ${seasons}. An import replaces every season, so add those weeks to the file, then choose it again.`,
            rowLabel: (row) => `Row ${row}`,
            rowTitle: (row, value) => value ? `Row ${row} · ${value}` : `Row ${row}`,
            readyRow: (code, week) => `${code} · week ${week}`,
            reason: {
                missing_value: 'This row has an empty cell. Fill it in the file.',
                unknown_variety: (code) => `${code} is not a variety code. Check the code in the file.`,
                unknown_season: (seasons) => `Use season ${seasons}.`,
                invalid_week: 'Use a whole week number from 1 to 53.',
                week_not_in_season: (week, season, valid) => `Week ${week} is not in season ${season}. Use weeks ${valid}.`,
            },
            duplicateRow: (row) => `Same as row ${row}.`,
            /** `2025/2026, 2026/2027 or 2027/2028` */
            joinOr: (head, last) => `${head} or ${last}`,
            replaceN: (n) => n === 0
                ? 'Replace harvest weeks'
                : n === 1
                    ? 'Replace with 1 harvest week'
                    : `Replace with ${n} harvest weeks`,
            confirmTitle: 'Replace variety harvest weeks?',
            confirmBody: (existing, n) => `The ${existing === 1 ? '1 variety harvest week' : `${existing} variety harvest weeks`} there now will be deleted and replaced by the ${n === 1 ? '1 week' : `${n} weeks`} in this file.`,
            confirmNote: 'The weeks deleted cannot be brought back, except by importing them again.',
            confirm: 'Replace all',
            replaced: (n) => n === 1
                ? 'Variety harvest weeks replaced. 1 week imported.'
                : `Variety harvest weeks replaced. ${n} weeks imported.`,
            failed: 'Nothing was changed. Try again when you have signal.',
        },
    },
    errors: {
        /** Shown when the API is reachable but rejects the request for a reason the grower cannot fix. */
        generic: 'That did not work. Try again in a moment.',
        sessionExpired: 'Your session has ended. Log in again.',
        editWindowClosed: 'This estimate can no longer be changed. Contact Fruitways if it is wrong.',
        notFound: 'That estimate is no longer here. Pull down to refresh.',
    },
};
//# sourceMappingURL=messages.js.map